import { any } from "prop-types";
import React, { useContext } from "react";
import { format } from 'date-fns';
import {
    IBusinessResultData,
    IBusinessData,
    IBusinessUnitAPIResponse,
    IDepartmentAPIResponse,
    IDepartmentsResultData,
    ILocationsResultData,
    ILocationAPIResponse,
    IAccessGroupsResultData,
    IAccessGroupsAPIResponse,
    IUsersAPIResponse,
    IUsersData,
    AssociationType,
    ISelectedDimensionsData,
    IStoredMappingData,
    IStoredDimension,
    ILocationsGetParms,
    IDimension,
    IDimensionOption,
    IAccessGroupFilter,
    INamedDimensionData,
    AddDimensionModalProps,
    ILocationsData,
    IDepartmentsData,
} from "./OdsTypes";
import { ListAppliedSearchFilterProps, ListAppliedSearchFilterValueProps, ListSearchFilterCustomProps } from "@pixel/core";

export const transformBUResultData = (resultSet: IBusinessUnitAPIResponse): IBusinessResultData => {
    return <IBusinessResultData>{
        nodes: resultSet.businessUnits,
        pageSize: resultSet.pageSize,
        totalCount: resultSet.totalCount,
        pageNumber: resultSet.pageNumber,
    };
}

export const isAvailableInBreadcrumb = (BUHierarchyTree: any, business: any) => {
    let isAlreadyInBreadcrumb: boolean = false;
    BUHierarchyTree.forEach((e: any) => {
        if (e.eid === business.eid) {
            isAlreadyInBreadcrumb = true;
        }
    });
    return isAlreadyInBreadcrumb;
}

export const transformDate = (formats: any[]) => {
    let newFormats: any = [];
    formats.forEach((date: string) => {
        newFormats.push(format(new Date(), date) + " - " + '(' + date + ')');
    });
    return newFormats;
}

export const transformTime = (formats: any[]) => {
    let newFormats: any = [];
    formats.forEach((date: string) => {
        let newDate = new Date();
        const newFormattedDate = date.replace('Z', 'XXX') // To support new formatting by date-fns
        newFormats.push(format(newDate.getTime(), newFormattedDate) + " - " + '(' + date + ')');
    });
    return newFormats;
}

export const updateBreadcrumb = (BUHierarchyTree: any, business: any) => {
    let newBreadcrumb: any = [];
    BUHierarchyTree.forEach((e: any, i: any) => {
        if (e.eid === business.eid) {
            newBreadcrumb = BUHierarchyTree.slice(0, i + 1)
            return;
        }
    });
    return newBreadcrumb;
}

export const filterOutCurrentParentBU = (buList: any, eid: string) => {
    let newParentBUList: any = [];
    buList.forEach((e: any, i: any) => {
        if (e.eid !== eid) {
            newParentBUList.push(e);
        }
    });
    return newParentBUList;
}

export const fecthWords = (name: string, numberOfWords: number) => {
    return name.split(' ').slice(0, numberOfWords).join(' ');
}

// Department transforms
export const transformDepartmentsResult = (resultSet: IDepartmentAPIResponse): IDepartmentsResultData => {
    return <IDepartmentsResultData>{
        nodes: resultSet?.departments ? resultSet?.departments : resultSet?.activeDepartments,
        pageSize: resultSet.pageSize,
        totalCount: resultSet.totalCount,
        pageNumber: resultSet.pageNumber,
    };
}

export const transformLocationsResult = (resultSet: ILocationAPIResponse): ILocationsResultData => {
    return <ILocationsResultData>{
        nodes: resultSet.locations,
        pageSize: resultSet.pageSize,
        totalCount: resultSet.totalCount,
        pageNumber: resultSet.pageNumber,
    };
}

type filter = {
    status?: string;
    createdByEIds?: string[];
};

export const transformLocationFilterData = (filterData: any) => {
    let updatedFilterPayload: filter = {};
    filterData.forEach((filter: any) => {
        if (filter.searchFilterId === 'createdByFilter') {
            updatedFilterPayload.createdByEIds = [];
            filter.filterValues.forEach((item: any) => {
                updatedFilterPayload.createdByEIds.push(item.value);
            })
        } else if (filter.searchFilterId === 'statusFilter') {
            updatedFilterPayload.status = filter.filterValues[0].value;
        }
    });
    return updatedFilterPayload;
}

// AccessGroup transforms
export const transformAccessGroupsResult = (resultSet: IAccessGroupsAPIResponse): IAccessGroupsResultData => {
    return <IAccessGroupsResultData>{
        nodes: resultSet.accessGroups,
        pageSize: resultSet.pageSize,
        totalCount: resultSet.totalCount,
        pageNumber: resultSet.pageNumber,
    };
}

// AccessGroup users
export const transformUsersResult = (resultSet: IUsersAPIResponse): IUsersData => {
    return <IUsersData>{
        nodes: resultSet.users,
        pageSize: resultSet.pageSize,
        totalCount: resultSet.totalCount,
        pageNumber: resultSet.pageNumber,
    };
}

/**
* getLocalStorageDimensionData fetches the localStorage list for dimensions 
*
* @param {string} dimensionKey - The localStorage dimensionKey
*/
export const getLocalStorageDimensionData = <T>(dimensionKey: string): T => {
    const storedData = localStorage.getItem(dimensionKey);
    return storedData ? JSON.parse(storedData) : {};
}

/**
* setLocalStorageDimensionData stores the localStorage list for dimensions 
*
* @param {string} dimensionKey - The localStorage dimensionKey
* @param {Array} data - The updated list
*/
export const setLocalStorageDimensionData = <T>(dimensionKey: string, data: T): void => {
    localStorage.setItem(dimensionKey, JSON.stringify(data));
}

/**
* getFullySelectedKeys from the provided data 
* Which is required for displaying the check/uncheck 
* status on the locations list.
*
* @param {Array} data - An array of locations objects.
*/
export const getFullySelectedKeys = (data: Record<string, any>[]) => {
    const fullySelectedKeys: string[] = [];
    for (const obj of data) {
        for (const key in obj) {
            if (obj[key].associationType === AssociationType.FULLY_ASSOCIATED) {
                if (!fullySelectedKeys.includes(key)) {
                    fullySelectedKeys.push(key);
                }
            }
        }
    }
    const fullySelectedKeysCopy = [...fullySelectedKeys];
    const concatenatedArray = Array.from(new Set([...fullySelectedKeysCopy]));
    return concatenatedArray;
}

/**
* updateDimensionLocalMapList Check if the data is stored in localStorage 
* based on which it sets the associationType and selectionCount.
* The dimensions are processed top-down, so the parent of a location always has the latest state.
* If parent is fully/not-assocaiated, blindly the dimension can take parent's state
* if parent is partially selected:
* * if dimension is fully/non-associated, dimensions state can be considered as the latest state
* * if dimension is partially-selected, no-child has been modified, dimension-state coming from backend or in the local storage is the same, 
* * * and anyone can be considered
* * if dimension is partially-selected and children are modified, in that case, we recalculate the state by seeing what has been modified in children
* @param {Array} list - An array of newly fetched dimension list
* @param {String} localStorageDimensionKey - The localStorage key for dimension.
* @returns {localList} The update list
*/
export const updateDimensionLocalMapList = (list: Array < ISelectedDimensionsData > , localStorageDimensionKey: string, childrenForModifiedPartiallySelectedRequestedIds: {
        [key: string]: Array < ISelectedDimensionsData >
    }) => {
        let localList: IStoredMappingData = getLocalStorageDimensionData(localStorageDimensionKey);
        localList.adjancencyList = localList.adjancencyList || {};
        localList.dimensionIdToDimensionList = localList.dimensionIdToDimensionList || {};
        let processedModifiedPartiallySelectedIds : Set<string> = new Set();
        list.forEach((dimension: ISelectedDimensionsData) => {
            if (localList.dimensionIdToDimensionList.hasOwnProperty(dimension.eid) && !(localList.dimensionIdToDimensionList[dimension.eid].parentEId === dimension.parentEId)) {
                const {
                    [dimension.eid]: removedKey, ...rest
                } = localList.dimensionIdToDimensionList;
                localList.dimensionIdToDimensionList = rest;
                let children = (localList.adjancencyList[localList.dimensionIdToDimensionList[dimension.eid].parentEId] || []).filter(item => item !== dimension.eid);
                localList.adjancencyList[localList.dimensionIdToDimensionList[dimension.eid].parentEId] = children;

            }
            if ((dimension.parentEId !== null) && localList.dimensionIdToDimensionList[dimension.parentEId].associationType !== AssociationType.PARTIALLY_ASSOCIATED) {
                localList.dimensionIdToDimensionList[dimension.eid] = {
                    associationType: localList.dimensionIdToDimensionList[dimension.parentEId].associationType,
                    selectionCount: localList.dimensionIdToDimensionList[dimension.parentEId].associationType === AssociationType.FULLY_ASSOCIATED ?
                        dimension.totalCount : 0,
                    partialSelectedCount: 0,
                    totalCount: dimension.totalCount,
                    parentEId: dimension.parentEId,
                    level: dimension.level
                }

            } else {
                if (!localList.dimensionIdToDimensionList.hasOwnProperty(dimension.eid)) {
                    localList.dimensionIdToDimensionList[dimension.eid] = {
                        associationType: dimension.associationType,
                        selectionCount: dimension.selectionCount,
                        partialSelectedCount: dimension.partialSelectedCount,
                        totalCount: dimension.totalCount,
                        parentEId: dimension.parentEId,
                        level: dimension.level
                    }
                } else if (localList.dimensionIdToDimensionList[dimension.eid].associationType !== AssociationType.PARTIALLY_ASSOCIATED) {
                    localList.dimensionIdToDimensionList[dimension.eid] = {
                        associationType: localList.dimensionIdToDimensionList[dimension.eid].associationType,
                        selectionCount: localList.dimensionIdToDimensionList[dimension.eid].associationType === AssociationType.FULLY_ASSOCIATED ?
                            dimension.totalCount : 0,
                        partialSelectedCount: 0,
                        totalCount: dimension.totalCount,
                        parentEId: dimension.parentEId,
                        level: dimension.level
                    }
                } else if (!(localList.adjancencyList[dimension.eid]) || localList.adjancencyList[dimension.eid].length == 0) {
                    localList.dimensionIdToDimensionList[dimension.eid] = {
                        associationType: dimension.associationType,
                        selectionCount: dimension.selectionCount,
                        partialSelectedCount: dimension.partialSelectedCount,
                        totalCount: dimension.totalCount,
                        parentEId: dimension.parentEId,
                        level: dimension.level
                    }

                } else {
                    if(!processedModifiedPartiallySelectedIds.has(dimension.eid)){
                        processSubtreeRecursive(dimension, childrenForModifiedPartiallySelectedRequestedIds, localList, processedModifiedPartiallySelectedIds);
                        const prevAssociatedData: IStoredMappingData = localList;
                        updateAssociationTypeOfAncestors(localList, dimension.eid, dimension.eid, prevAssociatedData);
                    }

                }
            }
            const newDimension = localList.dimensionIdToDimensionList[dimension.eid];
            addToModifiedAdjancencyList2(dimension, newDimension, localList);


        })


    setLocalStorageDimensionData(localStorageDimensionKey, localList);
    return getFullySelectedKeys([localList.dimensionIdToDimensionList]);;
}

/**
 * 
 * @param dimensionEId Recalcuates the selection count, partial selection count
 *  to account for change in children(dimension got deacivated, deleted, parent changed etc)
 * if a modified chidlren dimension of the current dimension is not longer an active child, we remove its data from local storage
 * @param localList 
 * @param childrenForModifiedPartiallySelectedRequestedIds 
 * @returns 
 */

export const processSubtreeRecursive = (dimension: ISelectedDimensionsData, childrenForModifiedPartiallySelectedRequestedIds: {[key: string]: Array < ISelectedDimensionsData >},  
    localList: IStoredMappingData, processedModifiedPartiallySelectedIds : Set<string>) : string => {

    const childrenModified: Array< string > = localList.adjancencyList[dimension.eid];
    let childrenFetched: {
        [key: string]: ISelectedDimensionsData
    } = childrenForModifiedPartiallySelectedRequestedIds[dimension.eid]
    .reduce((list: {
        [key: string]: ISelectedDimensionsData
    }, selectedDimensionsData: ISelectedDimensionsData) => {
        list[selectedDimensionsData.eid] = selectedDimensionsData;
        return list;
    }, {});
    let modifiedSelectionCount: number = 0;
    let modifiedPartialSelectionCount: number = 0;
    childrenModified.forEach((childModified: string) => {
                if (childrenFetched.hasOwnProperty(childModified)) {
                    let childAssociationType: string = localList.dimensionIdToDimensionList[childModified].associationType;
                    if (childAssociationType === AssociationType.PARTIALLY_ASSOCIATED) {
                        childAssociationType = processSubtreeRecursive(childrenFetched[childModified], childrenForModifiedPartiallySelectedRequestedIds,
                            localList, processedModifiedPartiallySelectedIds);
                    }
                    const {
                        selectionCount,
                        partialSelectedCount
                    } = updateSelectionCountsIfChildStateChanged(modifiedSelectionCount,modifiedPartialSelectionCount, childAssociationType, childrenFetched[childModified].associationType)
                    modifiedSelectionCount = selectionCount;
                    modifiedPartialSelectionCount = partialSelectedCount;
                    

                }else{
                    const {
                        [childModified]: removedKey, ...rest
                    } = localList.dimensionIdToDimensionList;
                    localList.dimensionIdToDimensionList = rest;
                    localList.adjancencyList[dimension.eid] = (localList.adjancencyList[dimension.eid] || []).filter(item => item !== childModified);
                }
            })
    const associationType = dimension.totalCount !== 0 ? updateParentAssociationType(dimension.totalCount,
        (dimension.selectionCount + modifiedSelectionCount), (dimension.partialSelectedCount + modifiedPartialSelectionCount)) : AssociationType.NOT_ASSOCIATED

    localList.dimensionIdToDimensionList[dimension.eid] = {
        selectionCount: dimension.selectionCount + modifiedSelectionCount,
        partialSelectedCount: dimension.partialSelectedCount + modifiedPartialSelectionCount,
        associationType: associationType,
        parentEId: dimension.parentEId,
        level: dimension.level,
        totalCount: dimension.totalCount
    };

    const newDimension = localList.dimensionIdToDimensionList[dimension.eid];
    addToModifiedAdjancencyList2(dimension, newDimension, localList);

    processedModifiedPartiallySelectedIds.add(dimension.eid);
    return associationType;

}

export const updateSelectionCountsIfChildStateChanged = (selectionCount: number, partialSelectedCount: number, newState: string, oldState: string): {
    selectionCount: number,
    partialSelectedCount: number
} => {
    if (!(newState === oldState)) {
        switch (newState) {
            case AssociationType.FULLY_ASSOCIATED:
                selectionCount++
                break;

            case AssociationType.PARTIALLY_ASSOCIATED:
                partialSelectedCount++;
                break;

        }
        switch (oldState) {
            case AssociationType.FULLY_ASSOCIATED:
                selectionCount--;
                break;

            case AssociationType.PARTIALLY_ASSOCIATED:
                partialSelectedCount--;
                break;
        }
    }

    return {
        selectionCount,
        partialSelectedCount
    };

}

/**
* updateParentAssociationType Checks all the nested parents based on parentEId
* if associationType is fullySelected and selectionCount is equal to totalCount
* sets the current node associationType
*
* @param {IStoredMappingData} data - Dimension list form localStorage

* @param {String} eid - Eid to be matched with parentEid
* @param {boolean} selectionState - if its a check or uncheck event
* @returns {AssociationType} The selection type of the parent
*/
export const updateParentAssociationType = (totalCount: number, selectionCount: number, partialSelectedCount: number): AssociationType => {
    if (selectionCount === totalCount) {
        return AssociationType.FULLY_ASSOCIATED;
    } else if (selectionCount !== 0 || partialSelectedCount !== 0) {
        return AssociationType.PARTIALLY_ASSOCIATED;
    } else {
        return AssociationType.NOT_ASSOCIATED;
    }
}

/**
* updateAssociationTypeRecursively Checks all the nested childs based on eid
* if associationType is fullySelected and selectionCount is equal to totalCount
* sets the current node associationType
*
* @param {IStoredMappingData} data - Dimension list form localStorage
* @param {String} parentId - Eid to be matched with parentEid
* @param {boolean} selectionState - if its a check or uncheck event
* @returns {updatedData} The list with update associationType and selectionCount
*/
export const updateAssociationTypeRecursively = (
    data: IStoredMappingData,
    parentId: string | null,
    selectionState: boolean
): IStoredMappingData => {
    const updatedData: IStoredMappingData = { ...data };

    for (const key in updatedData.dimensionIdToDimensionList) {
        if (updatedData.dimensionIdToDimensionList[key].parentEId === parentId) {
            updatedData.dimensionIdToDimensionList[key].associationType = selectionState ? AssociationType.FULLY_ASSOCIATED : AssociationType.NOT_ASSOCIATED;
            updatedData.dimensionIdToDimensionList[key].selectionCount = selectionState ? updatedData.dimensionIdToDimensionList[key].totalCount : 0;
            updatedData.dimensionIdToDimensionList[key].partialSelectedCount = 0;

            addToModifiedAdjancencyList(data, key, updatedData);

            const children = updateAssociationTypeRecursively(
                updatedData,
                key,
                selectionState
            );
            updatedData.dimensionIdToDimensionList[key] = { ...updatedData.dimensionIdToDimensionList[key], ...children.dimensionIdToDimensionList[key] };
        }
    }
    return updatedData;
};

/**
 * 
 * @param prevAssociatedData 
 * @param nextAssociatedData 
 * @param eid 
 * @returns Object with all the associated states
 */

const getAssociatedStates = (prevAssociatedData: IStoredMappingData, nextAssociatedData: IStoredMappingData, eid: string) => {
    const isPrevStateFullyAssociated = prevAssociatedData?.dimensionIdToDimensionList?.[eid]?.associationType === AssociationType.FULLY_ASSOCIATED;
    const isPrevStatePartiallyAssociated = prevAssociatedData?.dimensionIdToDimensionList?.[eid]?.associationType === AssociationType.PARTIALLY_ASSOCIATED;

    const isNextStateFullyAssociated = nextAssociatedData?.dimensionIdToDimensionList?.[eid]?.associationType === AssociationType.FULLY_ASSOCIATED;
    const isNextStatePartiallyAssociated = nextAssociatedData?.dimensionIdToDimensionList?.[eid]?.associationType === AssociationType.PARTIALLY_ASSOCIATED;

    return { 
        isPrevStateFullyAssociated,
        isPrevStatePartiallyAssociated,
        isNextStateFullyAssociated,
        isNextStatePartiallyAssociated,
    };
}

/**
* getSelectionCount checks if the AssociationType is FullySelected, 
* Unselected or Partially Selected. Based on which update the selectionCount.
*
* @param {string} associationType - Dimension list form localStorage
* @param {IStoredDimension} node - Eid to be matched with parentEid
* @returns {selectionCount} The selectionCount
*/
export const getSelectionCount = (node: IStoredDimension, currentNodeEid: string, nextAssociatedData: IStoredMappingData, prevAssociatedData: IStoredMappingData) => {
    const { 
        isNextStateFullyAssociated,
        isPrevStateFullyAssociated
    } = getAssociatedStates(prevAssociatedData, nextAssociatedData, currentNodeEid);

    if (isNextStateFullyAssociated) {
        return node?.selectionCount + 1;
    } else if (isPrevStateFullyAssociated) {
        return node?.selectionCount - 1;
    } else {
        return node?.selectionCount;
    }
}

export const getPartialSelectedCount = (node: IStoredDimension, currentNodeEid: string, nextAssociatedData: IStoredMappingData, prevAssociatedData: IStoredMappingData) => {
    const { 
        isPrevStatePartiallyAssociated,
        isNextStatePartiallyAssociated
    } = getAssociatedStates(prevAssociatedData, nextAssociatedData, currentNodeEid);

    if (isPrevStatePartiallyAssociated && isNextStatePartiallyAssociated) {
        return node?.partialSelectedCount;
    } else if (isNextStatePartiallyAssociated) {
        return node?.partialSelectedCount + 1;
    } else if (isPrevStatePartiallyAssociated) {
        return node?.partialSelectedCount - 1;
    } else {
        return node?.partialSelectedCount;
    }
}

/**
 * Gets Partially Selected Ids which have been modified(in turn whose children have been selected or unselected)
 * @param partiallySelectedDimensionIds 
 * @param dimensionKey 
 * @returns 
 */

export const getPartiallySelectedModifiedIds = (partiallySelectedDimensionIds : string[], dimensionKey: string) : Array<string> => {
    let modifiedPartiallySelectedIds : Array<string> = [];
    if(partiallySelectedDimensionIds && (partiallySelectedDimensionIds.length !== 0)){
        let localList: IStoredMappingData = getLocalStorageDimensionData(dimensionKey);
        let adjancencyModifiedList : {[key : string]: Array<string>}= localList.adjancencyList || {};
        partiallySelectedDimensionIds.forEach((partiallySelectedDimensionId : string) => {
            if(adjancencyModifiedList[partiallySelectedDimensionId] && (adjancencyModifiedList[partiallySelectedDimensionId].length !== 0)){
                modifiedPartiallySelectedIds.push(partiallySelectedDimensionId);
            }
        })
    }
    return modifiedPartiallySelectedIds;
}

/**
* updateLocalSelectionState Manipulates the localStorage data to update
* the nested Parent hirerchay and the nested child hirerchay. Store the 
* updated list in localStorage again.
*
* @param {string} nodeEid - list item eid for which has been clicked
* @param {boolean} selectionState - if its a check or uncheck event
* @returns {localList} The update list
*/
export const updateLocalSelectionState = (nodeEid: string, selectionState: boolean, dimensionKey: string, prevAssociatedData: IStoredMappingData) => {
    let localList: IStoredMappingData = getLocalStorageDimensionData(dimensionKey);
    localList.dimensionIdToDimensionList = localList.dimensionIdToDimensionList || {};
    let selectedNodeEid = nodeEid;
    let currentNodeEid = nodeEid;
    if (localList.dimensionIdToDimensionList.hasOwnProperty(nodeEid)) {
        localList.dimensionIdToDimensionList[nodeEid] = {
            associationType: selectionState ? AssociationType.FULLY_ASSOCIATED : AssociationType.NOT_ASSOCIATED,
            selectionCount: !selectionState ? 0 : localList.dimensionIdToDimensionList[nodeEid].totalCount,
            totalCount: localList.dimensionIdToDimensionList[nodeEid].totalCount,
            parentEId: localList.dimensionIdToDimensionList[nodeEid].parentEId,
            partialSelectedCount: 0,
        }
    }
    addToModifiedAdjancencyList(prevAssociatedData, nodeEid, localList);
    // itterates on parent hirerachey to bubble up the associationType and selectedCount
    updateAssociationTypeOfAncestors(localList,nodeEid, currentNodeEid, prevAssociatedData);
    // itterates on child hirerachey to bubble down the associationType and selectedCount
    localList = updateAssociationTypeRecursively(localList, selectedNodeEid, selectionState);
    setLocalStorageDimensionData(dimensionKey, localList);
    return getFullySelectedKeys([localList.dimensionIdToDimensionList]);
}

/**
 * itterates on parent hirerachey to bubble up the associationType and selectedCount
 * @param localList
 * @param nodeEid
 * @param currentNodeEid
 * @param prevAssociatedData
 */
export const updateAssociationTypeOfAncestors = (localList: IStoredMappingData, nodeEid: string, currentNodeEid: string, prevAssociatedData: IStoredMappingData) => {
        // itterates on parent hirerachey to bubble up the associationType and selectedCount
        while (localList.dimensionIdToDimensionList[nodeEid]?.parentEId !== null) {
            nodeEid = localList.dimensionIdToDimensionList[nodeEid].parentEId;
            const selectionCount = getSelectionCount(localList?.dimensionIdToDimensionList?.[nodeEid], currentNodeEid, localList, prevAssociatedData);
            const partialSelectedCount = getPartialSelectedCount(localList?.dimensionIdToDimensionList?.[nodeEid], currentNodeEid, localList, prevAssociatedData);
            localList.dimensionIdToDimensionList[nodeEid] = {...localList.dimensionIdToDimensionList[nodeEid], selectionCount};
            localList.dimensionIdToDimensionList[nodeEid] = {...localList.dimensionIdToDimensionList[nodeEid], partialSelectedCount};
            const associationType = updateParentAssociationType(localList.dimensionIdToDimensionList[nodeEid].totalCount, selectionCount,partialSelectedCount);
            currentNodeEid = nodeEid;
            if (localList.dimensionIdToDimensionList.hasOwnProperty(nodeEid)) {
                localList.dimensionIdToDimensionList[nodeEid] = {
                    selectionCount,
                    associationType,
                    totalCount: localList.dimensionIdToDimensionList[nodeEid].totalCount,
                    parentEId: localList.dimensionIdToDimensionList[nodeEid].parentEId,
                    partialSelectedCount,
                };
            } else {
                break;
            }

            addToModifiedAdjancencyList(prevAssociatedData, nodeEid, localList);
        }

}

/**
 * 
 * @param dimensionKey 
 * @returns selectedDimensionData
 */

export const getSelectedDimensionData = (dimensionKey: string) => {
    const selectedDimensionData : IStoredMappingData = getLocalStorageDimensionData(dimensionKey);
    selectedDimensionData.dimensionIdToDimensionList = selectedDimensionData.dimensionIdToDimensionList || {};
    return selectedDimensionData.dimensionIdToDimensionList && Object.keys(selectedDimensionData.dimensionIdToDimensionList)?.map((eidKey) => {
        const {associationType, parentEId, selectionCount, totalCount} = selectedDimensionData?.dimensionIdToDimensionList?.[eidKey];
        return {
            eid: eidKey,
            associationType,
            parentEId,
            selectionCount,
            totalCount,
          }
    });
}

export const getSelectedSubDimensionsCount = (eid: string, dimensionKey: string) => {
    const selectedDimensionData : IStoredMappingData = getLocalStorageDimensionData(dimensionKey);
    selectedDimensionData.dimensionIdToDimensionList = selectedDimensionData.dimensionIdToDimensionList || {};
    return selectedDimensionData?.dimensionIdToDimensionList?.[eid]?.selectionCount;
}

export const transformDimensionDropdownData = (dimensionList:Array<IDimension>) => {
    const updatedDimensionList: Array<IDimensionOption> = [];
    dimensionList?.forEach((dimension) => {
        const dimensionOption = {
            label: dimension.name,
            value: dimension.eid,
        }
        updatedDimensionList.push(dimensionOption);
    });
    return updatedDimensionList;
}

export const transformAccessGroupFilterData = (filterData: Array<ListAppliedSearchFilterProps>) => {
    const defaultFilterPayload: IAccessGroupFilter = {
        locationsFilter: null,
        departmentsFilter: null,
        businessUnitsFilter: null,
        status: '',
    }

    let updatedFilterPayload: IAccessGroupFilter = defaultFilterPayload;
    
    filterData?.forEach((filter: ListAppliedSearchFilterProps) => {
        switch(filter?.searchFilterId) {
            case 'locationsFilter':
                updatedFilterPayload.locationsFilter = [];
                filter?.filterValues?.forEach((item: any) => {
                    updatedFilterPayload.locationsFilter.push(item.value);
                });
                break;
            case 'departmentsFilter':
                updatedFilterPayload.departmentsFilter = [];
                filter?.filterValues?.forEach((item: any) => {
                    updatedFilterPayload.departmentsFilter.push(item.value);
                });
                break;
            case 'businessUnitsFilter':
                updatedFilterPayload.businessUnitsFilter = [];
                filter?.filterValues?.forEach((item: any) => {
                    updatedFilterPayload.businessUnitsFilter.push(item.value);
                });
                break;
            case 'statusFilter':
                updatedFilterPayload.status = String(filter?.filterValues[0].value);
                break;
            default: updatedFilterPayload = defaultFilterPayload;
        }
    });

    return updatedFilterPayload;
}

export const transformAppliedAccessGroupFilterObject = (searchFilter: ListSearchFilterCustomProps, appliedFilterValuesOptions: Array<ListAppliedSearchFilterValueProps>) => {
    const appliedFiltersObject: ListAppliedSearchFilterProps = {
        searchFilterId: searchFilter.id,
        displayLabel: `${searchFilter.label}: ${appliedFilterValuesOptions?.[0]?.label}`,
        filterValues: [],
    };
    
    appliedFilterValuesOptions?.forEach((option) => {
    const filterOption: ListAppliedSearchFilterValueProps = {
        value: option.value,
        label: option.label,
    }
    appliedFiltersObject.filterValues.push(filterOption);
    });
    return appliedFiltersObject;
}

export const getPartiallySelectedDimensionIds = (dimensionKey: string) => {
    const localDimensionData: IStoredMappingData = getLocalStorageDimensionData(dimensionKey);
    localDimensionData.dimensionIdToDimensionList = localDimensionData.dimensionIdToDimensionList || {};

    return localDimensionData.dimensionIdToDimensionList && Object.keys(localDimensionData.dimensionIdToDimensionList)?.map((key) => {
        if (localDimensionData?.dimensionIdToDimensionList[key]?.associationType === AssociationType.PARTIALLY_ASSOCIATED) {
            return key;
        }
    })?.filter((key) => key);
}

/**
 * if a dimension has been modified, selectionCount, partialSelectionCount,selectionState has been modified, we keep track of it
 * @param data 
 * @param key 
 * @param updatedData 
 */

function addToModifiedAdjancencyList2(oldDimension: ISelectedDimensionsData, newDimension: IStoredDimension, updatedData: IStoredMappingData) {

    if ((oldDimension.selectionCount !== newDimension.selectionCount) ||
        (oldDimension.partialSelectedCount !== newDimension.partialSelectedCount) ||
        (oldDimension.associationType !== newDimension.associationType)) {
        updatedData.adjancencyList[oldDimension.parentEId] = updatedData.adjancencyList[oldDimension.parentEId] || [];
        updatedData.adjancencyList[oldDimension.parentEId].push(oldDimension.eid);
        updatedData.adjancencyList[oldDimension.parentEId] = Array.from(new Set(updatedData.adjancencyList[oldDimension.parentEId]));
    }
}

/**
 * if a dimension has been modified, selectionCount, partialSelectionCount,selectionState has been modified, we keep track of it
 * @param data 
 * @param key 
 * @param updatedData 
 */

export function addToModifiedAdjancencyList(data: IStoredMappingData, key: string, updatedData: IStoredMappingData) {
    const oldDimension : IStoredDimension = data.dimensionIdToDimensionList[key];
    const newDimension : IStoredDimension = updatedData.dimensionIdToDimensionList[key];
    if ((oldDimension.selectionCount !== newDimension.selectionCount) ||
        (oldDimension.partialSelectedCount !== newDimension.partialSelectedCount) ||
        (oldDimension.associationType !== newDimension.associationType)) {
        updatedData.adjancencyList[oldDimension.parentEId] = updatedData.adjancencyList[oldDimension.parentEId] || [];
        updatedData.adjancencyList[oldDimension.parentEId].push(key);
        updatedData.adjancencyList[oldDimension.parentEId] = Array.from(new Set(updatedData.adjancencyList[oldDimension.parentEId]));
    }
}

const transformNamedDimensionList = (data: Array<INamedDimensionData>) => {
    return data?.map((obj: INamedDimensionData) => {
        return { eid: obj?.eid, name: obj?.name, level: obj?.level, preSelected: obj?.preSelected }
    });
}

export const updateNamedDimensionsList = (keyForNamedDimensions: string, data: Array<INamedDimensionData>) => {
    const existingNamedDimensionsList = JSON.parse(localStorage.getItem(keyForNamedDimensions)) || [];

    data?.forEach((newObj: INamedDimensionData)  => {
      const objectExists = existingNamedDimensionsList?.some((obj: INamedDimensionData) => obj?.eid === newObj?.eid);

      if (!objectExists) {
        existingNamedDimensionsList?.push({ ...newObj, preSelected: false });
      }
    });

    localStorage.setItem(keyForNamedDimensions, JSON.stringify(transformNamedDimensionList(existingNamedDimensionsList) || []));
}

export const mergePreselectedAndActiveDimensions = (data: Array<INamedDimensionData>, keyForNamedDimensions: string) => {
    const namedDimensions = JSON.parse(localStorage.getItem(keyForNamedDimensions)) || [];

    if (namedDimensions?.length > 0) {
        updateNamedDimensionsList(keyForNamedDimensions, data);
    } else {
        const updatedData = data?.map((obj) => ({ ...obj, preSelected: true }));
        localStorage.setItem(keyForNamedDimensions, JSON.stringify(transformNamedDimensionList(updatedData) || []));
    }
}

export const getFullySelectedDimensions = (data: Array<ISelectedDimensionsData>) => {
    const fullySelectedDimensions = data?.map((data) => {
        if (data?.associationType === AssociationType.FULLY_ASSOCIATED) {
            return data;
        }
    });

    return fullySelectedDimensions;
}

export const getSelectedDimensionsToShow = (dimensionKey: string, keyForNamedDimensions: string) => {
    const namedDimensions = JSON.parse(localStorage.getItem(keyForNamedDimensions)) || [];
    const selectedData: Array<ISelectedDimensionsData> = getSelectedDimensionData(dimensionKey);
    const fullySelectedDimensions: Array<ISelectedDimensionsData> = getFullySelectedDimensions(selectedData);
    const selectedDimensionsToShow: Array<INamedDimensionData> = [...namedDimensions];

    if (fullySelectedDimensions?.length > 0) {
        const localList: IStoredMappingData = getLocalStorageDimensionData(dimensionKey);
        localList.dimensionIdToDimensionList = localList.dimensionIdToDimensionList || {};
  
        namedDimensions?.forEach((newObj: INamedDimensionData)  => {
          const objectExists = selectedDimensionsToShow?.some((obj: INamedDimensionData) => obj?.eid === newObj?.eid);
      
          if (!objectExists) {
            selectedDimensionsToShow?.push(newObj);
          }
        });
        
        return selectedDimensionsToShow?.filter((obj1) => {
            if (localList?.dimensionIdToDimensionList?.[obj1?.eid]) {
                return (localList?.dimensionIdToDimensionList?.[obj1?.eid]?.associationType === AssociationType.FULLY_ASSOCIATED);
            } else if (obj1?.preSelected) {
                return obj1;
            }
        });
    } else {
        return namedDimensions;
    }
}

export const sortSelectedDimensions = (dimensionKey: string, keyForNamedDimensions: string) => {
    const data = getSelectedDimensionsToShow(dimensionKey, keyForNamedDimensions);
    const sortedSelectedDimensions = data
      ?.sort(
        (dimension1: INamedDimensionData, dimension2: INamedDimensionData) =>
        dimension1?.level - dimension2?.level || dimension1?.name.localeCompare(dimension2?.name)
      );

    return sortedSelectedDimensions;
}

export const getDynamicLengthForDimensions = (selectedDimensions: string[], nodes: Array<ILocationsData> | Array<IBusinessData> | Array<IDepartmentsData>) => {
    let length = 0;

    if (!selectedDimensions || !nodes || !selectedDimensions?.length || !nodes?.length) return 0;
    
    selectedDimensions?.forEach((eid) => {
        if (nodes?.some((node) => eid === node?.eid)) {
            length++;
        }
    });

    return length;
}

export const handleSelectAllDimensions = (eventType: string, dimensionKey: string, prevAssociatedData: IStoredMappingData) => {
    const localList: IStoredMappingData = getLocalStorageDimensionData(dimensionKey);

    Object.keys(localList?.dimensionIdToDimensionList)?.forEach((node) => {
        if (eventType === 'selectAll') {
            localList.dimensionIdToDimensionList[node].associationType = AssociationType.FULLY_ASSOCIATED;
            localList.dimensionIdToDimensionList[node].selectionCount = localList?.dimensionIdToDimensionList?.[node]?.totalCount;
        } else if (eventType === 'unselectAll') {
            localList.dimensionIdToDimensionList[node].associationType = AssociationType.NOT_ASSOCIATED;
            localList.dimensionIdToDimensionList[node].selectionCount = 0;
        }
        addToModifiedAdjancencyList(prevAssociatedData, node, localList);
    });

    return localList;
}

export interface IGenericPayload {
    [key: string]: any;
}

export const checkForAnyChangesInForm = <T extends IGenericPayload>(
    initialState: T,
    newState: T,
    columnsToSkip: string[] = []
): boolean => {
    return Object.keys(initialState).some((key) => {
        if (columnsToSkip.includes(key)) return false;
        let initialValue = initialState[key];
        let newValue = newState[key];
        // Normalize string values by removing '[Copy] ' and 'copy_'
        const normalizeValue = (value: any) => 
            typeof value === 'string' ? value.replace('[Copy] ', '').replace('copy_', '') : value;
        initialValue = normalizeValue(initialValue);
        newValue = normalizeValue(newValue);
        // Consider '' and null as equivalent
        if ((initialValue === '' && newValue === null) || (initialValue === null && newValue === '')) {
            return false;
        }
        // Compare arrays by sorting them
        if (Array.isArray(initialValue) && Array.isArray(newValue)) {
            const sortedInitialValue = initialValue?.slice().sort().join(',') || '';
            const sortedNewValue = newValue?.slice().sort().join(',') || '';
            return sortedInitialValue !== sortedNewValue;
        }
        // Handle null/empty and array comparison
        if ((!initialValue && Array.isArray(newValue) && !newValue.length) || 
            (!newValue && Array.isArray(initialValue) && !initialValue.length)) {
            return false;
        }
        return initialValue !== newValue;
    });
};
